import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { api, unwrap } from "../client";
import type { components } from "../generated/schema";
import { noErrorToast, type AppMutationMeta } from "../mutationMeta";
import type { ItemDetail, MovePreview, SaveVariables } from "../types";

type S = components["schemas"];

export type ItemFilters = {
  q?: string;
  category?: string;
  kind?: "material" | "service" | "allowance";
  /** A company manufacturer id: only its items. */
  manufacturer?: string;
  status?: "active" | "archived" | "all";
  /** "true": only items bought from vendors. */
  purchased?: "true" | "false";
  stocked?: "true" | "false";
  sold?: "true" | "false";
  /** Comma-separated describing categories: items classified in each (or one inside it). */
  classified?: string;
  /** "true": active items missing a required classification. */
  needs_classification?: "true";
  page?: number;
};
export type ItemCreate = S["ItemWriteRequest"];
export type ItemWrite = S["PatchedItemWriteRequest"];
export type CategoryCreate = S["ItemCategoryWriteRequest"];
export type CategoryWrite = S["PatchedItemCategoryWriteRequest"];
export type AttributeCreate = S["ItemAttributeWriteRequest"];
export type AttributeWrite = S["PatchedItemAttributeWriteRequest"];

const keys = {
  categories: (slug: string) => ["tenant", slug, "item-categories"] as const,
  attributes: (slug: string) => ["tenant", slug, "item-attributes"] as const,
  items: (slug: string) => ["tenant", slug, "items"] as const,
  item: (slug: string, id: string) => ["tenant", slug, "items", "detail", id] as const,
};

/** An item's detail query, refreshed when something else changes it (its photos). */
export const itemKey = keys.item;

/** The units of measure (public registry, cached for an hour). */
export function useUnits() {
  return useQuery({ queryKey: ["units"], queryFn: () => unwrap(api.GET("/api/meta/units/")), staleTime: 60 * 60_000 });
}

/** Every product type (unpaginated; build the tree from parent_id); `tree`: another tree's key or id, "" for every tree. */
export function useItemCategories(slug: string, status: "active" | "all" = "active", tree: string = "product_type") {
  return useQuery({
    queryKey: [...keys.categories(slug), status, tree],
    queryFn: () =>
      unwrap(api.GET("/api/t/{tenant_slug}/item-categories/", { params: { path: { tenant_slug: slug }, query: { status, ...(tree ? { tree } : {}) } } })),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/** Every attribute definition of the company (a handful per category). */
export function useItemAttributes(slug: string) {
  return useQuery({
    queryKey: keys.attributes(slug),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/item-attributes/", { params: { path: { tenant_slug: slug } } })),
    staleTime: 30_000,
  });
}

export function useItems(slug: string, filters: ItemFilters, enabled = true) {
  return useQuery({
    queryKey: [...keys.items(slug), "list", filters],
    enabled,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/items/", { params: { path: { tenant_slug: slug }, query: filters } })),
    placeholderData: keepPreviousData,
  });
}

export function useItem(slug: string, id: string | undefined) {
  return useQuery({
    queryKey: keys.item(slug, id ?? "new"),
    enabled: Boolean(id),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/items/{id}/", { params: { path: { tenant_slug: slug, id: id! } } })),
  });
}

function useItemChanged(slug: string) {
  const qc = useQueryClient();
  return (item: ItemDetail) => {
    qc.setQueryData(keys.item(slug, item.id), item);
    qc.invalidateQueries({ queryKey: [...keys.items(slug), "list"] });
    qc.invalidateQueries({ queryKey: keys.categories(slug) }); // item counts
  };
}

export function useSaveItem(slug: string) {
  const changed = useItemChanged(slug);
  return useMutation({
    meta: noErrorToast,
    mutationFn: (vars: SaveVariables<ItemCreate, ItemWrite>) => {
      const path = { tenant_slug: slug };
      return vars.id !== undefined
        ? unwrap(api.PATCH("/api/t/{tenant_slug}/items/{id}/", { params: { path: { ...path, id: vars.id } }, body: vars.body }))
        : unwrap(api.POST("/api/t/{tenant_slug}/items/", { params: { path }, body: vars.body }));
    },
    onSuccess: changed,
  });
}

/** Restore shows the default error toast; pass `noErrorToast` where a ConfirmDialog renders the error (archive). */
export function useArchiveItem(slug: string, meta: AppMutationMeta = {}) {
  const changed = useItemChanged(slug);
  return useMutation({
    meta,
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) => {
      const params = { path: { tenant_slug: slug, id } };
      return archived
        ? unwrap(api.POST("/api/t/{tenant_slug}/items/{id}/archive/", { params }))
        : unwrap(api.POST("/api/t/{tenant_slug}/items/{id}/restore/", { params }));
    },
    onSuccess: changed,
  });
}

/** An edit that moves the category: editors send `parent_id` on an edit only when it changes. */
export const isMove = (vars: SaveVariables<unknown, { parent_id?: string | null }>) => vars.id !== undefined && vars.body.parent_id !== undefined;

export function useSaveCategory(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (vars: SaveVariables<CategoryCreate, CategoryWrite>) => {
      const path = { tenant_slug: slug };
      return vars.id !== undefined
        ? unwrap(api.PATCH("/api/t/{tenant_slug}/item-categories/{id}/", { params: { path: { ...path, id: vars.id } }, body: vars.body }))
        : unwrap(api.POST("/api/t/{tenant_slug}/item-categories/", { params: { path }, body: vars.body }));
    },
    onSuccess: (_saved, vars) => {
      qc.invalidateQueries({ queryKey: keys.categories(slug) });
      // A move changes the items' category paths, and may delete values that don't belong in the new place.
      if (isMove(vars)) qc.invalidateQueries({ queryKey: keys.items(slug) });
    },
  });
}

/**
 * What moving a category under `parentId` (null: top level) would do to its items' attribute values.
 * Always fetched fresh: the answer decides whether a move needs confirming.
 */
export function useMovePreview(slug: string) {
  const qc = useQueryClient();
  return useCallback(
    (id: string, parentId: string | null): Promise<MovePreview> =>
      qc.fetchQuery({
        queryKey: [...keys.categories(slug), "move-preview", id, parentId ?? ""],
        queryFn: () =>
          unwrap(
            api.GET("/api/t/{tenant_slug}/item-categories/{id}/move-preview/", {
              params: { path: { tenant_slug: slug, id }, query: { parent_id: parentId ?? "" } },
            }),
          ),
        staleTime: 0,
        gcTime: 0,
      }),
    [qc, slug],
  );
}

export function useArchiveCategory(slug: string, meta: AppMutationMeta = {}) {
  const qc = useQueryClient();
  return useMutation({
    meta,
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) => {
      const params = { path: { tenant_slug: slug, id } };
      return archived
        ? unwrap(api.POST("/api/t/{tenant_slug}/item-categories/{id}/archive/", { params }))
        : unwrap(api.POST("/api/t/{tenant_slug}/item-categories/{id}/restore/", { params }));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.categories(slug) }),
  });
}

export function useSaveAttribute(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (vars: SaveVariables<AttributeCreate, AttributeWrite>) => {
      const path = { tenant_slug: slug };
      return vars.id !== undefined
        ? unwrap(api.PATCH("/api/t/{tenant_slug}/item-attributes/{id}/", { params: { path: { ...path, id: vars.id } }, body: vars.body }))
        : unwrap(api.POST("/api/t/{tenant_slug}/item-attributes/", { params: { path }, body: vars.body }));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.attributes(slug) }),
  });
}

// --- manufacturers ------------------------------------------------------------------------------
const manufacturerKeys = {
  all: (slug: string) => ["tenant", slug, "manufacturers"] as const,
};

export function useManufacturers(slug: string, filters: { q?: string; status?: "active" | "archived" | "all"; page?: number }) {
  return useQuery({
    queryKey: [...manufacturerKeys.all(slug), "list", filters],
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/manufacturers/", { params: { path: { tenant_slug: slug }, query: filters } })),
    placeholderData: keepPreviousData,
  });
}

/** One of the company's manufacturers, with its linked K-SHOP directory entry. */
export function useManufacturer(slug: string, id: string) {
  return useQuery({
    queryKey: [...manufacturerKeys.all(slug), "detail", id],
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/manufacturers/{id}/", { params: { path: { tenant_slug: slug, id } } })),
  });
}

/** Picker suggestions: the company's manufacturers, then K-SHOP directory entries. */
export function useManufacturerOptions(slug: string, q: string, enabled: boolean) {
  return useQuery({
    queryKey: [...manufacturerKeys.all(slug), "options", q],
    enabled,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/manufacturers/options/", { params: { path: { tenant_slug: slug }, query: { q } } })),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

export type DirectoryFilters = { q?: string; trade_line?: string; page?: number };

/** The K-SHOP manufacturer directory entries the company hasn't added yet (paginated). */
export function useManufacturerDirectory(slug: string, filters: DirectoryFilters, enabled = true) {
  return useQuery({
    queryKey: [...manufacturerKeys.all(slug), "directory", filters],
    enabled,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/manufacturers/directory/", { params: { path: { tenant_slug: slug }, query: filters } })),
    placeholderData: keepPreviousData,
  });
}

/** Add by name or pick a directory entry; answers with the company's record (existing or new). */
export function useAddManufacturer(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: S["ManufacturerWriteRequest"]) =>
      unwrap(api.POST("/api/t/{tenant_slug}/manufacturers/", { params: { path: { tenant_slug: slug } }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: manufacturerKeys.all(slug) }),
  });
}

export function useUpdateManufacturer(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: ({ id, body }: { id: string; body: S["PatchedManufacturerWriteRequest"] }) =>
      unwrap(api.PATCH("/api/t/{tenant_slug}/manufacturers/{id}/", { params: { path: { tenant_slug: slug, id } }, body })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: manufacturerKeys.all(slug) });
      qc.invalidateQueries({ queryKey: keys.items(slug) });
    },
  });
}

/** Restore shows the default error toast; pass `noErrorToast` where a ConfirmDialog renders the error (archive). */
export function useArchiveManufacturer(slug: string, meta: AppMutationMeta = {}) {
  const qc = useQueryClient();
  return useMutation({
    meta,
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) => {
      const params = { path: { tenant_slug: slug, id } };
      return archived
        ? unwrap(api.POST("/api/t/{tenant_slug}/manufacturers/{id}/archive/", { params }))
        : unwrap(api.POST("/api/t/{tenant_slug}/manufacturers/{id}/restore/", { params }));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: manufacturerKeys.all(slug) }),
  });
}

/**
 * Link a company manufacturer to a K-SHOP directory entry (`entryId`), or unlink it (`entryId: null`).
 * An unlinked record is never linked again automatically; only picking an entry links it.
 */
export function useManufacturerLink(slug: string, meta: AppMutationMeta = {}) {
  const qc = useQueryClient();
  return useMutation({
    meta,
    mutationFn: ({ id, entryId }: { id: string; entryId: string | null }) => {
      const params = { path: { tenant_slug: slug, id } };
      return entryId
        ? unwrap(api.POST("/api/t/{tenant_slug}/manufacturers/{id}/link/", { params, body: { catalog_manufacturer_id: entryId } }))
        : unwrap(api.POST("/api/t/{tenant_slug}/manufacturers/{id}/unlink/", { params }));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: manufacturerKeys.all(slug) });
      qc.invalidateQueries({ queryKey: keys.items(slug) }); // items show their manufacturer
    },
  });
}

/** Merge a manufacturer into another of the company's; answers with the one kept (ADR-005). */
export function useMergeManufacturer(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: ({ id, intoId }: { id: string; intoId: string }) =>
      unwrap(api.POST("/api/t/{tenant_slug}/manufacturers/{id}/merge/", { params: { path: { tenant_slug: slug, id } }, body: { into_id: intoId } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: manufacturerKeys.all(slug) });
      qc.invalidateQueries({ queryKey: keys.items(slug) }); // items move to the kept manufacturer
    },
  });
}

/** Values the company already uses for the text attributes of a category. */
export function useAttributeSuggestions(slug: string, category: string) {
  return useQuery({
    queryKey: [...keys.attributes(slug), "suggestions", category],
    enabled: Boolean(category),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/item-attributes/suggestions/", { params: { path: { tenant_slug: slug }, query: { category } } })),
    staleTime: 60_000,
  });
}

// --- the K-SHOP product catalog, as a company adds from it (ADR-006) -------------------------------
export type CatalogProductFilters = { q?: string; trade_line?: string; manufacturer?: string; page?: number };
export type CatalogProduct = S["CatalogProductOption"];
export type FromCatalogRow = S["FromCatalogRowRequest"];

/** Validated K-SHOP products, with the company's own item for each (`item`). */
export function useCatalogProducts(slug: string, filters: CatalogProductFilters, enabled = true) {
  return useQuery({
    queryKey: [...keys.items(slug), "catalog", filters],
    enabled,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/catalog-products/", { params: { path: { tenant_slug: slug }, query: filters } })),
    placeholderData: keepPreviousData,
  });
}

/** The validated product a New item form's identifiers name (`product`, null when none). */
export function useCatalogMatch(slug: string, query: { manufacturer_id?: string; part_number?: string; gtin?: string }, enabled: boolean) {
  return useQuery({
    queryKey: [...keys.items(slug), "catalog-match", query],
    enabled,
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/catalog-products/match/", { params: { path: { tenant_slug: slug }, query } })),
    staleTime: 60_000,
  });
}

/** Add validated products as items, all or none. */
export function useAddFromCatalog(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (items: FromCatalogRow[]) => unwrap(api.POST("/api/t/{tenant_slug}/items/from-catalog/", { params: { path: { tenant_slug: slug } }, body: { items } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.items(slug) });
      qc.invalidateQueries({ queryKey: keys.categories(slug) });
      qc.invalidateQueries({ queryKey: ["tenant", slug, "manufacturers"] }); // records added for their makers
    },
  });
}
